import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { compare_name_asc } from '../../utils/utils';
dayjs.extend(dayOfYear);

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
noDataToDisplay(Highcharts);

type Props = {
  data: any;
};

const FinancialComparison = ({ data }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState(null);
  const chartComponent = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    //if (data?.hasOwnProperty('values') && data?.hasOwnProperty('times')) {
    prepareHighcharts(prepareData());
    //}
  }, [data]);

  const prepareData = () => {
    let opex = [];
    let capex = [];
    let cleaning_gains = [];
    let npv = [];

    data.sort(compare_name_asc).forEach((item) => {
      // Convert from USD to KUSD (divide by 1000)
      opex.push(Math.round((item?.opex?.total / 1000) * 100) / 100);
      capex.push(Math.round((item?.capex?.total / 1000) * 100) / 100);
      cleaning_gains.push(Math.round((item?.cleaning_gains / 1000) * 100) / 100);
      npv.push(Math.round((item?.net_present_value / 1000) * 100) / 100);
    });

    const maxNPV = Math.max(...npv);
    let timeseries_data = [
      {
        name: 'NPV CAPEX (KUSD)',
        data: capex,
      },
      {
        name: 'NPV OPEX (KUSD)',
        data: opex,
      },
      {
        name: 'NPV Cleaning Gains (KUSD)',
        data: cleaning_gains,
      },
      {
        name: 'NPV (KUSD)',
        data: npv,
        type: 'line',
        dataLabels: {
          enabled: true,
          useHTML: true,
          allowOverlap: true,
          formatter: function () {
            if (this.point.y === maxNPV) {
              return '<span style="opacity: 1 !important;">🏆</span>';
            }
            return this.point.y;
          },
          style: {
            textAlign: 'center',
            color: '#000',
            fontSize: '24px',
            x: 0,
            y: -10,
          },
        },
        // yAxis: 1,
      },
    ];
    return timeseries_data;
  };

  const prepareHighcharts = (highChartsData) => {
    const chart = {
      chart: {
        type: 'column',
      },
      title: {
        text: 'Financial Comparison',
        align: 'left',
      },
      xAxis: {
        categories: [...data.sort(compare_name_asc).map((item) => item.name)],
      },
      yAxis: [
        {
          title: {
            text: '(KUSD)',
          },
          // stackLabels: {
          //   enabled: true,
          // },
        },
        // {
        //   title: {
        //     text: 'NPV',
        //   },
        //   // opposite: true,
        // },
      ],
      // legend: {
      //   align: 'left',
      //   x: 70,
      //   verticalAlign: 'top',
      //   y: 30,
      //   floating: true,
      //   backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || 'white',
      //   borderColor: '#CCC',
      //   borderWidth: 1,
      //   shadow: false,
      // },
      tooltip: {
        headerFormat: '<b>{point.x}</b><br/>',
        pointFormat: '{series.name}: {point.y}', //<br/>Total: {point.stackTotal}'
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
          },
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ /*display: 'flex',*/ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          data && (
            <>
              <HighchartsReact
                ref={chartComponent}
                containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
                highcharts={Highcharts}
                options={chartOptions}
              />
            </>
          )
        )}
      </Box>
    </>
  );
};

export default FinancialComparison;
